import React, { useEffect, useState, useRef } from "react";
import {
  AddSendMessage,
  GetAllChatRoomMessages,
  GetAllChatRooms,
  GetAllInstructorsofThePurchaseMsg,
} from "../../../api";
import ErrorAlert from "../../../commonFunctions/Alerts/ErrorAlert";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  Grid,
  Badge,
  useTheme,
  ListItemButton,
  Container,
  useMediaQuery,
  Fade
} from "@mui/material";

// Icons
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MessageIcon from '@mui/icons-material/Message';

function Messages() {
  // Theme & Layout Logic
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [selectedUser, setSelectedUser] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [selectedCourse, setselectedCourse] = useState("");
  const [messageTextAdd, setmessageTextAdd] = useState("");
  const [roomMessages, setroomMessages] = useState([]);
  const [selectedChatCode, setselectedChatCode] = useState("");
  const [chatRooms, setchatRooms] = useState([]);
  const [selectedStudentCode, setselectedStudentCode] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Auto-scroll ref
  const messagesEndRef = useRef(null);

  // --- RED THEME PALETTE ---
  const colors = {
    primary: "#e53935",
    primaryDark: "#c62828",
    lightRed: "#ffebee",
    bgGradientStart: "#fafafa",
    bgGradientEnd: "#ffebee",
    chatBg: "#f4f6f8",
    bubbleOther: "#ffffff",
    textSelf: "#ffffff",
    textOther: "#1c1e21",
    timestamp: "#9e9e9e",
  };

  // --- STYLES ---
  const styles = {
    pageWrapper: {
      background: `linear-gradient(135deg, ${colors.bgGradientStart} 0%, ${colors.bgGradientEnd} 100%)`,
      height: "100vh",
      paddingTop: "2rem",
      paddingBottom: "2rem",
      marginLeft: isSmallScreen ? "0px" : "260px",
      transition: "margin-left 0.3s ease",
      width: isSmallScreen ? "100%" : "calc(100% - 260px)",
      display: "flex",
      flexDirection: "column",
    },
    // Red Gradient Header
    headerCard: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
      color: "white",
      borderRadius: "20px",
      padding: "2rem",
      marginBottom: "2rem",
      boxShadow: "0 10px 40px rgba(229, 57, 53, 0.3)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    sidebar: {
      borderRight: "1px solid #e0e0e0",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      bgcolor: "#fff",
    },
    chatArea: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      bgcolor: colors.chatBg,
      backgroundImage: "radial-gradient(#e0e0e0 1px, transparent 1px)",
      backgroundSize: "20px 20px",
    },
    messageList: {
      flex: 1,
      overflowY: "auto",
      p: 3,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    inputArea: {
      p: 2,
      bgcolor: "white",
      borderTop: "1px solid #f0f0f0",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      boxShadow: "0 -4px 20px rgba(0,0,0,0.02)"
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [roomMessages]);

  // Handle Clicking a user in the sidebar
  const handleUserClick = (user) => {
    setSelectedUser(user.student);
    setselectedCourse(user.courseCode);
    setselectedChatCode(user.chatRoomCode);
    setselectedStudentCode(user.studentUserCode);

    setroomMessages([]);
    setLoadingMessages(true);

    GetAllChatRoomMessages(user.chatRoomCode, (msgs) => {
      setroomMessages(msgs);
      setLoadingMessages(false);
    });
  };

  // Poll for new messages
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedChatCode) {
        GetAllChatRoomMessages(selectedChatCode, setroomMessages);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedChatCode]);

  // Load initial data
  useEffect(() => {
    GetAllChatRooms(setchatRooms);
  }, []);

  // Send Message Logic
  const handleSelectedMessageSend = (e) => {
    e.preventDefault();
    if (!selectedStudentCode || !messageTextAdd) {
      ErrorAlert("Empty Field", "Please enter a message.");
      return;
    }
    AddSendMessage(
      selectedStudentCode,
      messageTextAdd,
      selectedCourse,
      selectedChatCode,
      setmessageTextAdd,
      GetAllChatRooms,
      setchatRooms
    );
  };

  // Filter Logic
  const filteredChatRooms = chatRooms.filter((room) =>
    room.student.toLowerCase().includes(userFilter.toLowerCase())
  );

  return (
    <Box style={styles.pageWrapper}>
      <Container maxWidth="xl" sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
        
        {/* Red Gradient Header */}
        <Fade in timeout={500}>
          <Box style={styles.headerCard}>
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MessageIcon fontSize="large" /> Messages
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                Chat with your students in real-time
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Main Card */}
        <Card
          sx={{
            flex: 1,
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
            display: "flex",
            maxHeight: "calc(100vh - 220px)", 
            border: "1px solid rgba(0,0,0,0.05)"
          }}
        >
          <Grid container sx={{ height: "100%" }}>
            
            {/* --- LEFT SIDEBAR --- */}
            <Grid item xs={12} md={4} lg={3} sx={styles.sidebar}>
              {/* Search Bar */}
              <Box p={2.5} sx={{ borderBottom: "1px solid #f0f0f0" }}>
                <TextField
                  fullWidth
                  placeholder="Search chats..."
                  variant="outlined"
                  size="small"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#9e9e9e" }} />
                      </InputAdornment>
                    ),
                    sx: { 
                        borderRadius: "30px", 
                        bgcolor: "#f5f5f5", 
                        border: "none",
                        fontSize: "0.95rem",
                        paddingLeft: "10px"
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                />
              </Box>

              {/* User List */}
              <List sx={{ overflowY: "auto", flex: 1, padding: 0 }}>
                {filteredChatRooms.length > 0 ? (
                  filteredChatRooms.map((user, index) => {
                    const isSelected = selectedChatCode === user.chatRoomCode;
                    return (
                      <React.Fragment key={index}>
                        <ListItemButton
                          onClick={() => handleUserClick(user)}
                          selected={isSelected}
                          sx={{
                            padding: "16px 24px",
                            borderLeft: isSelected ? `4px solid ${colors.primary}` : "4px solid transparent",
                            bgcolor: isSelected ? colors.lightRed : "transparent",
                            transition: "all 0.2s",
                            "&.Mui-selected": {
                              bgcolor: colors.lightRed,
                              "&:hover": { bgcolor: "#ffcdd2" },
                            },
                            "&:hover": { bgcolor: "#f9f9f9" },
                          }}
                        >
                          <ListItemAvatar>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              variant="dot"
                              color="success"
                            >
                              <Avatar
                                alt={user.student}
                                src="/static/images/avatar/1.jpg"
                                sx={{ 
                                    width: 48, 
                                    height: 48, 
                                    border: isSelected ? `2px solid ${colors.primary}` : "1px solid #eee",
                                    bgcolor: "#bdbdbd" 
                                }}
                              >
                                {user.student.charAt(0)}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" fontWeight={isSelected ? "700" : "600"} noWrap>
                                  {user.student}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block" color={colors.primary} fontWeight="500" sx={{ mt: 0.5 }}>
                                  {user.courseTitle}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  noWrap
                                  sx={{ maxWidth: "180px", fontSize: "0.85rem", opacity: 0.8 }}
                                >
                                  {user.lastMessage}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                        <Divider variant="inset" component="li" sx={{ opacity: 0.5 }} />
                      </React.Fragment>
                    );
                  })
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    color="text.secondary"
                    p={4}
                    textAlign="center"
                  >
                    <SearchIcon sx={{ fontSize: 60, opacity: 0.1, mb: 2 }} />
                    <Typography>No conversations found</Typography>
                  </Box>
                )}
              </List>
            </Grid>

            {/* --- RIGHT CONTENT --- */}
            <Grid item xs={12} md={8} lg={9} sx={styles.chatArea}>
              {selectedUser !== "" ? (
                <>
                  {/* Chat Header - Removed Call/Video Icons */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "white",
                      zIndex: 10
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, bgcolor: colors.primary, width: 40, height: 40 }}>
                        {selectedUser.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                          {selectedUser}
                        </Typography>
                        <Typography variant="caption" color="green" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box component="span" sx={{width: 8, height: 8, bgcolor: 'green', borderRadius: '50%'}} />
                           Online
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Header Actions - Just Menu */}
                    <Box>
                        <IconButton><MoreVertIcon /></IconButton>
                    </Box>
                  </Paper>

                  {/* Messages Area */}
                  <Box sx={styles.messageList}>
                    {loadingMessages ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="textSecondary">Loading conversation...</Typography>
                      </Box>
                    ) : (
                      <>
                        {roomMessages.map((message, index) => {
                          const isMe = message.from !== selectedUser; 
                          
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: isMe ? "flex-end" : "flex-start",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: "70%",
                                  p: "12px 18px",
                                  borderRadius: isMe ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
                                  // Gradient for user, white for other
                                  background: isMe 
                                    ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` 
                                    : colors.bubbleOther,
                                  color: isMe ? colors.textSelf : colors.textOther,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  position: "relative",
                                  fontSize: "0.95rem",
                                  lineHeight: 1.5
                                }}
                              >
                                {message.message}
                              </Box>
                            </Box>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </Box>

                  {/* Input Area - Removed Attachment/Emoji Icons */}
                  <Box component="form" onSubmit={handleSelectedMessageSend} sx={styles.inputArea}>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      variant="outlined"
                      size="small"
                      value={messageTextAdd}
                      onChange={(e) => setmessageTextAdd(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "24px",
                          bgcolor: "#f5f5f5",
                          paddingLeft: "15px",
                          paddingRight: "10px",
                          "& fieldset": { border: "none" },
                          "&:hover": { bgcolor: "#eeeeee" }
                        },
                      }}
                    />
                    
                    <IconButton
                      type="submit"
                      disabled={!messageTextAdd.trim()}
                      sx={{
                        background: messageTextAdd.trim() 
                            ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` 
                            : "#e0e0e0",
                        color: "white",
                        width: 45,
                        height: 45,
                        transition: "all 0.2s",
                        "&:hover": { 
                            boxShadow: "0 4px 12px rgba(229, 57, 53, 0.4)",
                            transform: "scale(1.05)"
                        },
                        "&:disabled": { background: "#e0e0e0", color: "#fff" }
                      }}
                    >
                      <SendIcon fontSize="small" sx={{ ml: 0.5 }} />
                    </IconButton>
                  </Box>
                </>
              ) : (
                // Empty State
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  color="text.secondary"
                  sx={{ opacity: 0.6 }}
                >
                  <Box
                    sx={{
                      width: 140,
                      height: 140,
                      bgcolor: "#e0e0e0",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 70, color: "white" }} />
                  </Box>
                  <Typography variant="h5" fontWeight="700" gutterBottom>
                    Your Messages
                  </Typography>
                  <Typography variant="body1">
                    Select a conversation from the left to start chatting.
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
}

export default Messages;